export function zipLongest<T1, T2>(
  one: readonly T1[],
  two: readonly T2[],
): [T1 | undefined, T2 | undefined][] {
  return Array.from({ length: Math.max(one.length, two.length) }, (_, i) => [one[i], two[i]])
}

export const zipShortest = <A, B>(firstArray: A[], secondArray: B[]): [A, B][] => {
  const minLength = Math.min(firstArray.length, secondArray.length)

  return Array.from({ length: minLength }, (_, i) => [firstArray[i], secondArray[i]])
}

export const constructDefinedArray = <T>(...args: T[]): NonNullable<T>[] =>
  args.filter((item): item is NonNullable<T> => item != null)

type SliceMethod<T> = (start?: number | undefined, end?: number | undefined) => T
type Sliceable<T> = { length: number; slice: SliceMethod<T> }

export const chunkArray = <T extends Sliceable<T>>(array: T, chunkSize: number) =>
  Array.from({ length: Math.ceil(array.length / chunkSize) }, (_, index) => index * chunkSize).map(
    begin => array.slice(begin, begin + chunkSize),
  )

type Predicate<T> = (item: T, index: number, arr: T[]) => boolean
type SeparatedArray<T> = {
  pass: T[]
  fail: T[]
}

export function separateByPredicate<T>(array: T[], predicate: Predicate<T>): SeparatedArray<T> {
  const separated: SeparatedArray<T> = { pass: [], fail: [] }

  array.forEach((item, index, arr) => {
    if (predicate(item, index, arr)) {
      separated.pass.push(item)
    } else {
      separated.fail.push(item)
    }
  })

  return separated
}

export const pairwise = <T, V>(arr: T[], map: (current: T, next: T) => V, skips = 1): V[] => {
  const result: V[] = []
  for (let i = 0; i < arr.length - skips; i++) {
    result.push(map(arr[i], arr[i + skips]))
  }
  return result
}

export const unique = <T>(...params: T[][]): T[] => Array.from(new Set<T>(params.flat()))
/**
 * @typeParam {K} -- вложенный массив, каждая строка матрицы
 * @typeParam {T} -- тип матрицы
 *
 * Если матрица -- массив массивов обычных типов, то K -- массив этих типов
 *
 * То есть
 * ```ts
 * type T = string[][]
 * type K = T[number] //=> string[]
 * type Item = K[number] //=> string
 * ```
 *
 * Если матрица -- массив TypedArray, то K -- не определен, нужно взять элемент T
 *
 * То есть
 * ```ts
 * type T = Float32Array[]
 * type K = ArrayLike<unknown>
 * type Item = T[number][number]
 * ```
 *
 */
type ItemOfArrayLikeMatrix<
  T extends ArrayLike<K>,
  K extends ArrayLike<unknown> = T[number],
> = K[number] extends unknown ? T[number][number][][] : K[number][][]

export const transpose = <K extends ArrayLike<unknown>, T extends ArrayLike<K>>(
  matrix: T,
): ItemOfArrayLikeMatrix<T> => {
  type Item = K[number]
  const rows = matrix.length
  const columns = matrix[0].length

  const transposed: Item[][] = []

  for (let i = 0; i < columns; i++) {
    transposed[i] = []
    for (let j = 0; j < rows; j++) {
      transposed[i][j] = matrix[j][i]
    }
  }

  return transposed
}

export const isValidIndex = (array: unknown[], index: number) => 0 <= index && index < array.length

export const convertWithKey = <T extends { id: string }>(values: T[]) => {
  return values.map(values => ({ ...values, key: values.id }))
}

/**
 * Groups elements from an iterable into an object based on a callback function.
 *
 * @template T, K
 * @param {Iterable<T>} iterable - The iterable to group.
 * @param {function(T, number): K} callbackfn - The callback function to
 * determine the grouping key.
 * @returns {Object.<string, T[]>} An object where keys are the grouping keys
 * and values are arrays of grouped elements.
 */
export function groupBy<T extends object, Key extends PropertyKey>(
  iterable: Iterable<T>,
  selector: (item: T, index: number) => Key,
): Partial<Record<Key, T[]>> {
  const obj = Object.create(null)
  let i = 0
  for (const value of iterable) {
    const key = selector(value, i++)
    if (key in obj) {
      obj[key].push(value)
    } else {
      obj[key] = [value]
    }
  }
  return obj
}

export const isEmpty = (array: unknown[]) => array.length === 0

export function uniqueBy<T, K>(iterable: Iterable<T>, key: (item: T) => K): T[] {
  const seen = new Set<K>()
  const result: T[] = []

  for (const item of iterable) {
    const keyValue = key(item)
    if (!seen.has(keyValue)) {
      seen.add(keyValue)
      result.push(item)
    }
  }

  return result
}

export function toggleInArray<T>(arr: T[], item: T): T[] {
  return arr.includes(item) ? arr.filter(i => i !== item) : [...arr, item]
}
